import React, { useState } from 'react';
import { ChipOption } from '@marageti/z4-lib';
import { SelectChangeEvent } from '@mui/material';
import { SalesRecordingStatus } from '@marageti/z4-sdk/lib/travel';
import { SalesRecordingType } from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { format, isValid, sub } from 'date-fns';
import AllSalesView from './all-sales-view';
import useURLSearchQuery from '../../../hooks/use-url-for-search-query';
import useSalesRecordingSearch from '../../../hooks/use-sales-recording-search';

type Option = {
  title: string;
  value: string;
};

const today = new Date();
const todayMinusTwoMonths = sub(today, { months: 2 });
const defaultPartnerOption = { title: 'All', value: 'all' };

const sortByOptions: Option[] = [
  { title: 'Recording Date', value: 'createdAt' },
  { title: 'Total Price', value: 'price.amount' },
];

const dateOrderByOptions: Option[] = [
  { title: 'New to Old', value: 'desc' },
  { title: 'Old to New', value: 'asc' },
];

const priceOrderByOptions: Option[] = [
  { title: 'High to Low', value: 'desc' },
  { title: 'Low to High', value: 'asc' },
];

const typeValues: { [key: string]: SalesRecordingType[] } = {
  standardSale: [SalesRecordingType.General],
  repeat: [SalesRecordingType.PartnerRepeat],
  referral: [SalesRecordingType.TravelerReferral],
  adjustment: [SalesRecordingType.Adjustment],
  other: [SalesRecordingType.PlanningFee, SalesRecordingType.CommissionAfterTravel, SalesRecordingType.RefundCredit],
};

const statusValues: { [key: string]: SalesRecordingStatus[] } = {
  invoiced: [SalesRecordingStatus.InvoicedStatus],
  uninvoiced: [SalesRecordingStatus.SoldStatus, SalesRecordingStatus.VerifiedStatus],
};

const chipTypeOptions: ChipOption[] = [
  { key: 0, label: 'Standard Sale', value: 'standardSale' },
  { key: 1, label: 'Repeat', value: 'repeat' },
  { key: 2, label: 'Referral', value: 'referral' },
  { key: 3, label: 'Adjustment', value: 'adjustment' },
  { key: 4, label: 'Other', value: 'other' },
];

const chipStatusOptions: ChipOption[] = [
  { key: 0, label: 'Invoiced', value: 'invoiced' },
  { key: 1, label: 'Uninvoiced', value: 'uninvoiced' },
];

interface DefaultSearch {
  [key: string]: string | string[];
  endDate: string;
  fullText: string;
  order: string;
  partnerId: string;
  salesRecordingType: string[];
  sort: string;
  startDate: string;
  status: string[];
}

const defaultSearch: DefaultSearch = {
  endDate: format(today, 'yyyy-MM-dd'),
  fullText: '',
  order: dateOrderByOptions[0].value,
  partnerId: defaultPartnerOption.value,
  salesRecordingType: [],
  sort: sortByOptions[0].value,
  startDate: format(todayMinusTwoMonths, 'yyyy-MM-dd'),
  status: [],
};

const MonthlySalesContainer = () => {
  const [queryValues, updateURLQuery] = useURLSearchQuery<DefaultSearch>(defaultSearch);
  const {
    endDate,
    fullText,
    order,
    partnerId,
    salesRecordingType,
    sort,
    startDate,
    status,
  } = queryValues;

  const {
    salesRecordings,
    error,
    hasMore,
    isLoading,
    loadMore,
  } = useSalesRecordingSearch(queryValues);

  const selectedTypeChips = Object.entries(typeValues).reduce((acc: string[], [key, value]) => {
    if (salesRecordingType.includes(value[0])) {
      return [...acc, key];
    }
    return acc;
  }, []);

  const selectedStatusChips = Object.entries(statusValues).reduce((acc: string[], [key, value]) => {
    if (status.includes(value[0])) {
      return [...acc, key];
    }
    return acc;
  }, []);

  // Keeps track of which orderByOptions to use based on selected sortBy
  const [orderByOptions, setOrderByOptions] = useState<Option[]>(dateOrderByOptions);

  const onChangeStartDay = (value: Date | null) => {
    // Check if the value is a valid Date object or null
    if (value === null || isValid(value)) {
      // const formattedStartDate = format(value || today, 'yyyy-MM-dd');
      updateURLQuery({ startDate: (value || today).toString() });
    } else {
      updateURLQuery({ startDate: todayMinusTwoMonths.toString() });
    }
  };

  const onChangeEndDay = (value: Date | null) => {
    // Check if the value is a valid Date object or null
    if (value === null || isValid(value)) {
      const formattedEndDay = value ? value.toString() : '';
      updateURLQuery({ endDate: formattedEndDay });
    } else {
      updateURLQuery({ endDate: today.toString() });
    }
  };

  const onChangePartner = (value: string) => {
    updateURLQuery({ partnerId: value });
  };

  const onChangeSortBy = (event: SelectChangeEvent) => {
    const v = event.target.value;
    const newSortBy = sortByOptions.find((o) => o.value === v) || sortByOptions[0];
    updateURLQuery({ sort: newSortBy.value });

    // Update orderByOptions based on selected sortBy
    if (newSortBy.value === 'price.amount') {
      setOrderByOptions(priceOrderByOptions);
    } else {
      setOrderByOptions(dateOrderByOptions);
    }
  };

  const onChangeOrderBy = (event: SelectChangeEvent) => {
    const v = event.target.value;
    const newOrderBy = (orderByOptions.find((o) => o.value === v) || orderByOptions[0]).value;
    updateURLQuery({ order: newOrderBy });
  };

  const onChipsChange = (value: string[], type: 'status' | 'salesRecordingType') => {
    if (type === 'status') {
      const values = value.map((v) => statusValues[v]);
      updateURLQuery({ status: values.flat() });
    }
    if (type === 'salesRecordingType') {
      const values = value.map((v) => typeValues[v]);
      updateURLQuery({ salesRecordingType: values.flat() });
    }
  };

  const onSearchChange = (newValue: string) => {
    updateURLQuery({ fullText: newValue });
  };

  return (
    <AllSalesView
      chipStatusOptions={chipStatusOptions}
      chipTypeOptions={chipTypeOptions}
      error={error}
      handleSearchInputChange={onSearchChange}
      hasMore={hasMore}
      loading={isLoading}
      loadMore={loadMore}
      onChangeEndDay={onChangeEndDay}
      onChangeOrderBy={onChangeOrderBy}
      onChangePartner={onChangePartner}
      onChangeSortBy={onChangeSortBy}
      onChangeStartDay={onChangeStartDay}
      onChipsChange={onChipsChange}
      orderBy={order}
      orderByOptions={orderByOptions}
      sales={salesRecordings || []}
      searchTerm={fullText}
      selectedEndDay={new Date(endDate)}
      selectedPartner={partnerId}
      selectedStartDay={new Date(startDate)}
      selectedStatusChips={selectedStatusChips}
      selectedTypeChips={selectedTypeChips}
      sortBy={sort}
      sortByOptions={sortByOptions}
    />
  );
};

export default MonthlySalesContainer;
